/**
 * Text formatting for material-input values, shared by the cell renderer and the differences
 * tooltip. Both surfaces must read identically — a cell showing "glucose (720 mL)" beside a tooltip
 * showing "glucose (720)" looks like a bug — so the amount unit is applied in exactly one place here.
 *
 * Stays free of React so `PMEICellTooltipContent.ts` can consume it without pulling the renderer's
 * component dependencies into its unit test.
 */
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import type {UnitSummary} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';

import type {MaterialInputValue} from './PMEICellColorGrade';

/**
 * An amount with its unit symbol appended ("720 mL"), or null when there is no amount to show. The
 * unit comes from the parameter version rather than the value, so an unconfigured unit yields the
 * bare number.
 */
export function formatMaterialInputAmount(
  amount: TypedValue | null | undefined,
  amountUnit: UnitSummary | null
): string | null {
  if (amount == null) {
    return null;
  }
  const amountText = formatTypedValue(amount);
  if (!amountText) {
    return null;
  }
  const unitSymbol = amountUnit?.symbol;
  return unitSymbol != null && unitSymbol !== '' ? `${amountText} ${unitSymbol}` : amountText;
}

/**
 * A whole material-input value as one line of text, mirroring the cell renderer's "material (amount)"
 * shape: material only → "glucose"; material with amount → "glucose (720 mL)"; amount alone (the
 * unplanned-amount case) → "(720 mL)".
 */
export function formatMaterialInputValue(value: MaterialInputValue, amountUnit: UnitSummary | null): string {
  const materialText = isTypedValueEmpty(value.material) ? '' : formatTypedValue(value.material);
  const amountText = formatMaterialInputAmount(value.amount, amountUnit) ?? '';
  if (amountText === '') {
    return materialText;
  }
  return materialText === '' ? `(${amountText})` : `${materialText} (${amountText})`;
}
